import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { bucketTopN } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { isValidMetalType } from "./_shared/isValidMetalType";

// data rows carry the raw label under "MetalType" + raw count under "COUNT"
// (exact SQL column casing from GetMetalTypeData, unlike Category's "count").
// Each row also carries MetalTypeID/MetalColorID, needed by onSliceClick to
// resolve the compound {metalTypeId, metalColorId} the table SP filters on.
const JewelryMetalTypeComp = ({ data, loading, onSliceClick, onChartTypeChange }) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState("doughnut");
  useEffect(() => { onChartTypeChange?.(chartType); }, [chartType]);
  const [hideNoise, setHideNoise] = useState(true);
  const chartRef = useRef();
  const list = data || [];

  const filteredList = useMemo(
    () => (hideNoise ? list.filter((item) => isValidMetalType(item.MetalType)) : list),
    [list, hideNoise]
  );
  const hiddenCount = list.length - filteredList.length;

  const { chartData, colors } = bucketTopN(filteredList, {
    labelKey: "MetalType",
    valueKey: "COUNT",
    chartLabel: "Metal Type",
    othersLabel: t("Others"),
  });
  const rows = chartData.slice(1);

  const handleClick = (event) => {
    if (!onSliceClick || !chartRef.current) return;
    const [el] = getElementAtEvent(chartRef.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (!label || label === t("Others")) return;
    const row = filteredList.find((item) => item.MetalType === label);
    if (row) onSliceClick({ metalTypeId: row.MetalTypeID, metalColorId: row.MetalColorID });
  };

  const chartJsData = {
    labels: rows.map((row) => row[0]),
    datasets: [
      {
        data: rows.map((row) => row[1]),
        backgroundColor: colors,
        borderColor: "#fff",
        borderWidth: chartType === "doughnut" ? 2 : 0,
        borderRadius: chartType === "bar" ? 4 : 0,
      },
    ],
  };

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <label>
          <b> {t("Jewelry Metal Type Clicks")} </b>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div className="edr-toggle">
            <span>{t("Hide None / unspecified")}</span>
            <label className="edr-switch">
              <input type="checkbox" checked={hideNoise} onChange={(e) => setHideNoise(e.target.checked)} />
              <span className="edr-switch-track"></span>
            </label>
            {hiddenCount > 0 && <span className="edr-badge edr-flag">{hiddenCount} {t("hidden")}</span>}
          </div>
          <div className="edr-seg">
            <button className={chartType === "doughnut" ? "edr-on" : ""} onClick={() => setChartType("doughnut")}>{t("Donut")}</button>
            <button className={chartType === "bar" ? "edr-on" : ""} onClick={() => setChartType("bar")}>{t("Bar")}</button>
          </div>
        </div>
      </div>
      <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
        {!loading && rows.length > 0 ? (
          <>
            {chartType === "doughnut" && <CustomLegend chartData={chartData} colorData={colors} />}
            <div className="edr-chartwrap edr-tall">
              {chartType === "doughnut" ? (
                <Doughnut ref={chartRef} data={chartJsData} options={donutOptions} onClick={handleClick} />
              ) : (
                <Bar ref={chartRef} data={chartJsData} options={barOptions(true)} onClick={handleClick} />
              )}
            </div>
          </>
        ) : (
          <ChartState loading={loading} />
        )}
      </div>
    </div>
  );
};

export default JewelryMetalTypeComp;
